import styled from '@emotion/styled';
import { theme } from '@travelnest/hui';
import { IconButton } from '@travelnest/merlin.button-icon';
import { Input } from '@travelnest/merlin.input';
import { Select } from '@travelnest/merlin.select';
import { range } from 'lodash';
import React from 'react';
import { MonthNames } from './date-helpers';
interface IMonthPaginationProps {
	current: Date;
	// The number of months you can select in the past
	rangeMonthsPast?: number;
	// The number of months you can select in the future
	rangeMonthsFuture?: number;
	next(): void;
	prev(): void;
	selectMonth(next: Date): void;
}

const Wrapper = styled.div`

	font-weight: 500;
	font-size: 2px;
	margin-bottom: ${theme.spacing.space60};

	display: flex;
	align-items: flex-start;
	width: 100%;
	justify-content: space-between;
	text-transform: capitalize;
	@media only screen and (min-width: 468px) {
		width: 335px;
	}

	& > div:first-child {
		flex: 1 1 70%;
		// Setting a width value here makes the flex controls work as I expect,
		// 190 is a good size to show the entire month
		width: 190px;
		margin-right: ${theme.spacing.space30};
	}

	& > div:last-child {
		flex: 2 2 30%;

		button:first-child {
			margin-right: 2.5px;
		}

		button:last-child {
			margin-left: 2.5px;
		}
	}
`;

const chevronCSS = `
	:before {
		content: "\\e901"; /* Arrow down */
		font-family: TravelNest;
		text-align: center;
		position: absolute;
		top: 0.75rem;
		right: 2px;
		pointer-events: none;
		z-index: 1;
		font-size: 2rem;
		color: ${theme.colors.LM_NU70};
	}
` ;

const SquareIconButton = styled(IconButton)`
	width: 100%;
	max-width: 45px;
	height: 45px;
`;

const InputWrapper = styled.div<{ hideChevron?: boolean }>`
	position: relative;

	${(props) => props.hideChevron ? '' : chevronCSS}
`;

// Title case the input string
const titleCase = (s: string) => {
	return `${s[0].toLocaleUpperCase()}${s.slice(1).toLocaleLowerCase()}`;
};

// Format a date for <input type="month">
const formatDate = (d: Date) => {
	return d.getFullYear().toString() + '-' + (d.getMonth() + 1).toString().padStart(2, '0');
};

const humanReadableFormat = (d: Date) => {
	return `${titleCase(MonthNames[d.getMonth()])} ${d.getFullYear()}`;
};

// Create a range of past & future values for <Select> <option>s
const getSelectMonthValues = (current: Date, past: number, future: number): Array<{ name: string, value: string }> => {
	const valueRange = range(past * -1, future, 1);

	return valueRange.map((v) => {
		const d = new Date(current);
		d.setMonth(v);
		return { name: humanReadableFormat(d), value: formatDate(d) };
	});
};

// _Attempt_ to detect Chrome on desktop.
// https://stackoverflow.com/a/13348618
// https://stackoverflow.com/questions/25393865/how-do-you-detect-between-a-desktop-and-mobile-chrome-user-agent
const isChromeDesktop = () => {
	const isChromium = (window as any).chrome !== null && (window as any).chrome !== undefined;
	const winNav = window.navigator;
	const vendorName = winNav.vendor;
	const isOpera = typeof (window as any).opr !== 'undefined';
	const isIEedge = winNav.userAgent.indexOf('Edge') > -1;
	const isMobileDevice = winNav.userAgent.match('/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i');

	return !isMobileDevice && isChromium && vendorName.toLocaleLowerCase().includes('google') && !isOpera && !isIEedge;
};

export const MonthPagination: React.FC<IMonthPaginationProps> = ({
	prev,
	next,
	current,
	selectMonth,
	rangeMonthsPast,
	rangeMonthsFuture
}) => {
	const currentFormatted = formatDate(current);
	// Either props, or 10 years
	const rangePast = rangeMonthsPast ?? 120;
	const rangeFuture = rangeMonthsFuture ?? 120;

	const [supportsMonthInput, setSupportsMonthInput] = React.useState(false);

	// Detect <input type="month" support>
	React.useLayoutEffect(() => {
		const i = document.createElement('input');
		i.setAttribute('type', 'month');
		setSupportsMonthInput(i.type !== 'text');
	}, []);

	const handleChange = (nextMonthRaw: string) => {
		const parts = nextMonthRaw.split('-');
		const d = new Date(`${parts[0]}-${parts[1]}-01`);
		selectMonth(d);
	};

	return (
		<Wrapper>
			<div>
				{(supportsMonthInput && !isChromeDesktop()) ?
					<InputWrapper>
						<Input id="month-select" type="month" value={currentFormatted} onChange={(e) => handleChange(e.target.value)} />
					</InputWrapper>
					:
					<Select id="month-select" value={currentFormatted} onChange={(e) => handleChange(e.target.value)}>
						{getSelectMonthValues(current, rangePast, rangeFuture).map((o) => (<option key={o.value} value={o.value}>{o.name}</option>))};
					</Select>
				}
			</div>
			<div style={{ display: 'flex' }}>
				<SquareIconButton
					icon="left"
					onClick={(e) => {
						e.preventDefault();
						prev();
					}}
					iconPosition={'center'}
					label="previous month"
					colour="darkGrey"
				/>
				<SquareIconButton
					icon="right"
					onClick={(e) => {
						e.preventDefault();
						next();
					}}
					iconPosition="center"
					label="next month"
					colour="darkGrey"
				/>
			</div>
		</Wrapper>
	);
};
